import React from 'react';
import { Link } from 'react-router-dom';
import PageShell from '../components/PageShell';
import Icon from '../components/Icon';
import ServiceGlyph from '../components/ServiceGlyph';
import Eyebrow from '../components/page/Eyebrow';
import { tie } from '../components/page/typography';
import { serviceMeta } from '../content/services';
import { PHONE } from '../content/contacts';
import { trackEvent } from '../utils/analytics';

/**
 * Сторінка 404.
 *
 * Раніше тут був лише перелік шести послуг, хоча лід обіцяв «куди можна
 * перейти». Але на неактуальну адресу частіше потрапляють зі старого
 * посилання на статтю чи питання, а не на послугу, — і такій людині перелік
 * послуг не допомагав. Тепер спершу три головні напрямки (послуги, питання,
 * контакти), під ними — самі послуги, і рядком — решта сторінок.
 *
 * Загального банера-заклику внизу немає: «Контакти» вже стоять першим рядом
 * і з номером телефону поруч, а другий такий самий заклик лише відсував би
 * переходи.
 */
const MAIN = [
  { to: '/poslugy', icon: 'fa-sitemap', title: 'Послуги', text: 'Реєстрація, ліцензія, ЕСОЗ, документи, торговельна марка й супровід' },
  { to: '/pytannya', icon: 'fa-comment-dots', title: 'Часті запитання', text: 'Відповіді на те, про що нас питають найчастіше' }
];

const MORE = [
  { to: '/', label: 'Головна' },
  { to: '/z-chogo-pochaty', label: 'З чого почати' },
  { to: '/plan-zapusku-kliniky', label: 'План запуску клініки' },
  { to: '/tsinova-polityka', label: 'Цінова політика' },
  { to: '/pro-kompaniyu', label: 'Про компанію' },
  { to: '/blog', label: 'Блог' }
];

const tileClass =
  'group flex h-full items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 lg:p-6 hover:border-teal-300 hover:shadow-md transition-[border-color,box-shadow] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2';

const NotFound = () => (
  <PageShell
    title="Сторінку не знайдено"
    description="Такої сторінки на сайті немає. Скористайтесь переліком послуг або поверніться на головну."
    path="/404"
    noindex
    crumbs={[{ name: 'Сторінку не знайдено' }]}
    heading="Такої сторінки немає"
    lead="Можливо, адресу набрано з помилкою або матеріал переїхав. Ось куди можна перейти звідси."
    cta={false}
  >
    {/* Без верхнього відступу: заголовок без банера й так закінчується
        власним, і два відступи поспіль давали порожню смугу на пів екрана. */}
    <div className="page-container pb-16 lg:pb-24">
      <nav aria-labelledby="kudy-dali-title">
        <h2 id="kudy-dali-title" className="sr-only">Куди перейти</h2>
        <ul className="list-none m-0 p-0 grid gap-4 md:grid-cols-3">
          {MAIN.map((item) => (
            <li key={item.to}>
              <Link to={item.to} className={tileClass}>
                <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center" aria-hidden="true">
                  <Icon name={item.icon} className="text-lg" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-3 font-display text-lg font-bold text-slate-900 group-hover:text-teal-800 transition-colors duration-200">
                    {item.title}
                    <Icon
                      name="fa-arrow-right"
                      className="flex-shrink-0 text-sm text-teal-700 transition-transform duration-200 motion-reduce:transition-none group-hover:translate-x-1"
                    />
                  </span>
                  <span className="mt-1 block text-[0.95rem] leading-relaxed text-slate-600">{tie(item.text)}</span>
                </span>
              </Link>
            </li>
          ))}

          {/* Контакти — виділені: хто не знайшов потрібного, найшвидше
              дізнається, де це, просто спитавши. Плитка не посилання, а
              картка з двома діями: вкладати телефон у посилання на сторінку
              контактів HTML не дозволяє, а окремим рядком під плиткою номер
              губився. */}
          <li className="relative overflow-hidden flex h-full items-start gap-4 rounded-2xl bg-gradient-to-r from-teal-700 to-sky-700 p-5 lg:p-6 text-white shadow-md">
            <span className="absolute -top-10 -right-8 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none" aria-hidden="true"></span>
            <span className="relative flex-shrink-0 w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center" aria-hidden="true">
              <Icon name="fa-paper-plane" className="text-lg" />
            </span>
            <div className="relative min-w-0 flex-1">
              <p className="font-display text-lg font-bold">Контакти</p>
              <div className="mt-1 flex flex-col items-start">
                <Link
                  to="/kontakty"
                  onClick={() => trackEvent('cta_click', '404')}
                  className="group inline-flex items-center gap-2 py-2 font-semibold text-white underline decoration-white/40 underline-offset-4 hover:decoration-white transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-sm"
                >
                  Написати нам
                  <Icon name="fa-arrow-right" className="text-sm transition-transform duration-200 motion-reduce:transition-none group-hover:translate-x-1" />
                </Link>
                <a
                  href={PHONE.href}
                  onClick={() => trackEvent('contact_channel', '404-phone')}
                  className="inline-flex items-center gap-2 py-2 font-semibold text-white whitespace-nowrap underline decoration-white/40 underline-offset-4 hover:decoration-white transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-sm"
                >
                  <Icon name="fa-phone" className="text-sm" />
                  {PHONE.label}
                </a>
              </div>
            </div>
          </li>
        </ul>
      </nav>

      <section aria-labelledby="napryamky-title" className="mt-12 lg:mt-14">
        <Eyebrow as="h2" id="napryamky-title" className="mb-5">
          Напрямки послуг
        </Eyebrow>
        <ul className="list-none m-0 p-0 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {serviceMeta.map((m, i) => (
            <li key={m.slug}>
              <Link
                to={`/poslugy/${m.slug}`}
                className="group flex h-full min-h-[3.75rem] items-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-teal-300 hover:shadow-sm transition-[border-color,box-shadow] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2"
              >
                <ServiceGlyph index={i} className="w-9 h-9 flex-shrink-0 text-teal-700" />
                <span className="flex-1 min-w-0 font-medium leading-snug text-slate-800 group-hover:text-teal-800 transition-colors duration-200">
                  {tie(m.title)}
                </span>
                <Icon name="fa-arrow-right" className="flex-shrink-0 text-xs text-teal-700" />
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <nav aria-labelledby="inshi-title" className="mt-12 lg:mt-14 pt-8 border-t border-slate-200">
        <Eyebrow as="h2" id="inshi-title" className="mb-4">
          Інші сторінки
        </Eyebrow>
        <ul className="list-none m-0 p-0 flex flex-wrap gap-2">
          {MORE.map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                className="inline-flex min-h-[2.75rem] items-center rounded-full border border-slate-200 px-4 text-[0.95rem] font-medium text-slate-700 hover:border-teal-300 hover:text-teal-800 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2"
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  </PageShell>
);

export default NotFound;
